import React, { useState, useEffect } from 'react';
import { BarChart3, Users, TrendingUp, ArrowRight, MessageSquare, Ear, Mail, Star, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

// Feedback items data outside component to avoid recreation on every render
const feedbackItems = [
  { source: "Email", icon: Mail, text: "Love the new dashboard! So intuitive", sentiment: "positive", delay: 600 },
  { source: "Chat", icon: MessageSquare, text: "Checkout process is confusing", sentiment: "negative", delay: 1200 },
  { source: "Review", icon: Star, text: "Great product, shipping was fast", sentiment: "positive", delay: 1800 },
  { source: "Survey", icon: ThumbsUp, text: "Pricing is a bit high for features offered", sentiment: "negative", delay: 2400 }
];

const CustomerInsightAgent = () => {
  const [feedback, setFeedback] = useState([]);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (feedback.length < feedbackItems.length) {
      const nextItem = feedbackItems[feedback.length];
      const timer = setTimeout(() => {
        setFeedback(prev => [...prev, nextItem]);
      }, nextItem.delay);
      return () => clearTimeout(timer);
    } else if (feedback.length === feedbackItems.length && !analysis) {
      const timer = setTimeout(() => {
        setAnalysis({
          sentiment: { positive: 50, negative: 50 },
          topIssue: "Checkout Experience",
          recommendation: "Simplify checkout flow, add progress indicators"
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [feedback.length, analysis]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30">
      <Header />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Customer Insights
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch AI collect feedback from every channel and deliver actionable insights instantly.
          </p>
        </div>

        {/* Animated Demo */}
        <div className="max-w-5xl mx-auto mb-12 md:mb-20">
          <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
            {/* Left: Feedback Collection */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 p-4 md:p-8">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">Collecting Feedback</h3>
                <div className="flex items-center space-x-1.5 md:space-x-2 text-xs md:text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live</span>
                </div>
              </div>
              
              <div className="space-y-3 md:space-y-4 max-h-[300px] md:max-h-[400px] overflow-y-auto">
                {feedback.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="animate-fadeIn bg-gray-50 rounded-2xl p-4 border border-gray-100">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-500">{item.source}</span>
                            {item.sentiment === 'positive' ? (
                              <ThumbsUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <ThumbsDown className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{item.text}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: AI Analysis */}
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 p-4 md:p-8">
              <div className="flex items-center space-x-2 mb-4 md:mb-6">
                <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                  <BarChart3 className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900">AI Analysis</h3>
              </div>

              {analysis ? (
                <div className="space-y-6 animate-fadeIn">
                  {/* Sentiment */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Sentiment Overview</h4>
                    <div className="flex space-x-2 mb-2">
                      <div className="flex-1 bg-green-100 rounded-full h-3 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500" style={{ width: `${analysis.sentiment.positive}%` }}></div>
                      </div>
                      <div className="flex-1 bg-red-100 rounded-full h-3 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-rose-500" style={{ width: `${analysis.sentiment.negative}%` }}></div>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>50% Positive</span>
                      <span>50% Negative</span>
                    </div>
                  </div>

                  {/* Top Issue */}
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-red-900 mb-1">Top Issue Detected</h4>
                        <p className="text-sm text-red-700">{analysis.topIssue}</p>
                      </div>
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-4">
                    <div className="flex items-start space-x-3">
                      <Ear className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1">Recommended Action</h4>
                        <p className="text-sm text-blue-700">{analysis.recommendation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-3 animate-pulse">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-gray-600">Analyzing feedback...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <MessageSquare className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Auto Feedback</h3>
            <p className="text-gray-600 leading-relaxed">
              Collect feedback from every channel automatically
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">AI Analysis</h3>
            <p className="text-gray-600 leading-relaxed">
              Understand sentiment and spot trends instantly
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Ear className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Actionable Insights</h3>
            <p className="text-gray-600 leading-relaxed">
              Get clear recommendations on what to fix next
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <BarChart3 className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Data-driven decisions</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <TrendingUp className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Improved retention</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <Users className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Better product fit</h3>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl p-16 shadow-xl">
          <h3 className="text-3xl font-bold text-[#2d4a1e] mb-4">
            Ready to understand your customers?
          </h3>
          <p className="text-lg text-[#2d4a1e]/80 mb-8 max-w-2xl mx-auto">
            Start analyzing feedback and improving your product
          </p>
          <a
            href="https://calendly.com/kolanut"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#2d4a1e] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d4a1e]/90 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CustomerInsightAgent;
